import React, {useCallback, useRef, useState, MouseEvent} from 'react';
import cx from 'classnames';
import {
    DropdownV2,
    Icon,
    ListItem,
    PriorityIcon,
    TriggerWrapperV2,
    Scrollbar
} from '@priority-software/priority-style-react';
import usePortal from 'react-useportal';
import {getBIData} from '../../actions/form';
import styles from './styles.module.scss';
import {useKeyboardNavigation} from '../../../../components/Common/FieldSearch/hooks/useKeyboardNavigation';
import {texts} from '../FormView/texts';
import {Tooltip} from '../../../../components/Common/Tooltip';
import {Column} from '../../../../stores/Form';

type Props = {
    dropdownWidth?: number;
    column: Column;
    disabled?: boolean;
    componentClasses?: string;
};

export const BIDropdown = (props: Props) => {
    const [showDropdown, setShowDropdown] = useState(false);
    const {dropdownWidth = 400, column, disabled, componentClasses} = props;
    const session = column.parentForm.session;

    const anchorRef = useRef<HTMLDivElement>(null);
    const [dropdownStyles, setDropdownStyles] = useState({});
    const [BIData, setBIData] = useState(null);
    const {Portal} = usePortal();

    const handleClose = useCallback(() => {
        setShowDropdown(false);
        setDropdownStyles({});
    }, []);

    const handleOpen = useCallback(
        async (e: MouseEvent<HTMLDivElement>) => {
            e.stopPropagation();
            const form = column.parentForm;

            if (form?.records[form.currentRow]?.keys) {
                const {top, left} = anchorRef.current.getBoundingClientRect() || {};

                setDropdownStyles({
                    position: 'fixed',
                    top: top + 8,
                    left: left + 10,
                    zIndex: 15000
                });

                setShowDropdown(true);

                if (!BIData) {
                    const data = await getBIData(session, column.id);

                    if (data) setBIData(data);
                }
            }
        },
        [BIData, session, column]
    );

    const {focusedIndex, handleKeyboardNavigation} = useKeyboardNavigation({
        initialFocusedIndex: 0,
        listLength: BIData?.length || 0,
        pageSize: 1,
        onSelect: () => handleSelect
    });

    const handleSelect = useCallback(() => {
        if (BIData) {
            alert(`handle ennter keydown on the ${BIData[focusedIndex]?.title}`);
        }
    }, [focusedIndex, BIData]);

    const handleItemClick = (index: number) => {
        const {title} = BIData[index];
        alert(`handle click on the ${title}`);
    };

    const handleKeydown = useCallback(
        e => {
            handleKeyboardNavigation(e);
        },
        [handleKeyboardNavigation]
    );

    return (
        <div
            className={cx(styles.component, {[componentClasses]: !!componentClasses})}
            ref={anchorRef}
        >
            <Tooltip
                position="top center"
                tooltip={texts.bi_icon_tooltip}
                className="new-ui-tooltip"
            >
                <Icon
                    icon={PriorityIcon.bi}
                    componentClasses={cx(styles.triggerIcon, {
                        [styles.disabled]: disabled
                    })}
                    onClick={handleOpen}
                />
            </Tooltip>

            {showDropdown && BIData && (
                <Portal>
                    <div className="reactComp new-ui-theme">
                        <DropdownV2
                            onKeyDown={handleKeydown}
                            onClose={handleClose}
                            onOpen={handleOpen}
                            isOpen={showDropdown}
                            openingSide={'rightSide'}
                            componentClasses={styles.biDropdown}
                            style={dropdownStyles}
                            focusOnOpen
                            trigger={
                                <TriggerWrapperV2 isOpen={showDropdown}>
                                    <Icon
                                        icon={PriorityIcon.bi}
                                        componentClasses={styles.triggerIcon}
                                        onClick={handleClose}
                                    />
                                </TriggerWrapperV2>
                            }
                        >
                            <div style={{width: dropdownWidth}}>
                                <Scrollbar autoHeight autoHeightMax={400}>
                                    {BIData
                                        ? BIData.map((item, index) => {
                                              return (
                                                  <ListItem
                                                      key={item.title}
                                                      isFocused={index === focusedIndex}
                                                      isActive={false}
                                                      onClick={() => handleItemClick(index)}
                                                  >
                                                      {item.title}
                                                  </ListItem>
                                              );
                                          })
                                        : null}
                                </Scrollbar>
                            </div>
                        </DropdownV2>
                    </div>
                </Portal>
            )}
        </div>
    );
};
